export class ClipboardUtils {
  static async copy(text: string): Promise<void> {
    await navigator.clipboard.writeText(text);
  }

  /**
   * カスタムフォーマットをサポートしているブラウザでは、カスタムフォーマットを使用してクリップボードにコピーします。そうでないブラウザでは、JSONをテキストとしてコピーします。
   */
  static async copyWithCustomFormat(params: { text?: string; json: string }): Promise<void> {
    const isSupportCustomFormat = ClipboardItem.supports?.('web text/plain') ?? false; // SafariではClipboardItem.supportsメソッド自体が無い

    const clipboardItem = isSupportCustomFormat
      ? new ClipboardItem({
          'web text/plain': new Blob([params.json], { type: 'text/plain' }),
          ...(params.text ? { 'text/plain': new Blob([params.text], { type: 'text/plain' }) } : {}),
        })
      : new ClipboardItem({
          'text/plain': new Blob([params.json], { type: 'text/plain' }),
        });

    await navigator.clipboard.write([clipboardItem]);
  }

  /**
   * カスタムフォーマットのMIMEタイプを使用してクリップボードからJSONを読み込みます。出来ない場合は通常のテキストを読み込みます。
   */
  static async readCustomFormatJSON(): Promise<string> {
    const clipboardItems = await navigator.clipboard.read();
    for (const clipboardItem of clipboardItems) {
      if (clipboardItem.types.includes('web text/plain')) {
        return (await clipboardItem.getType('web text/plain')).text();
      }
    }

    for (const clipboardItem of clipboardItems) {
      if (clipboardItem.types.includes('text/plain')) {
        return (await clipboardItem.getType('text/plain')).text();
      }
    }

    return '';
  }

  /**
   * ビューモデルにペーストする際のクリップボードのデータの読み込みを行う
   */
  static async readClipboardPayload(): Promise<
    | {
        type: 'custom';
        text: string;
      }
    | {
        type: 'images';
        images: File[];
      }
    | {
        type: 'text';
        text: string;
      }
    | { type: 'empty' }
  > {
    // カスタムフォーマットのデータがある場合はカスタムフォーマットのデータを読み込む
    const clipboardItems = await navigator.clipboard.read();
    for (const clipboardItem of clipboardItems) {
      if (clipboardItem.types.includes('web text/plain')) {
        return { type: 'custom', text: await (await clipboardItem.getType('web text/plain')).text() };
      }
    }

    // TSVやテキストがあればテキストを優先して読み込む
    // excelなどからコピーした場合、TSV形式のテキストが含まれているため、TSVやテキストがあればテキストを優先して読み込む
    let text: string | null = null;
    for (const clipboardItem of clipboardItems) {
      if (clipboardItem.types.includes('text/plain')) {
        text = await (await clipboardItem.getType('text/plain')).text();
      }
    }
    if (text !== '' && text !== null) {
      return { type: 'text', text };
    }

    // 画像ファイルがある場合は画像ファイルを読み込む
    {
      const files = [];
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            const file = new File([blob], `clipboard-image.${type.split('/')[1]}`, { type });
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        return { type: 'images', images: files };
      }
    }

    // 空の結果を返す
    return {
      type: 'empty',
    };
  }
}
